export const translations = {
  en: {
    // Navbar
    nav: {
      home: 'Home',
      projects: 'Projects',
      skills: 'Skills',
      experience: 'Experience'
    },
    // Home page
    home: {
      greeting: 'Hi, My Name is',
      name: 'Teoh Jia Kang',
      headline: 'AI-Native Product Engineer',
      bio: 'I build complete products across four disciplines — mechanical design in Fusion 360, electronics from schematic to PCB, software from firmware to cloud, and production AI systems. From a multi-tenant WhatsApp AI SaaS to industrial IoT telemetry, I take products the whole way: business problem, architecture, code, and live operation — deployed on servers and networks I run, under domains I own.',
      ctaPrimary: 'View Case Studies',
      ctaSecondary: 'Browse All Projects',
      openTo: 'Open to Opportunities',
      featuredTitle: 'Featured Work',
      featuredSubtitle: 'Products and systems I designed, built and operate',
      viewCaseStudy: 'View Case Study',
      statsProducts: 'Products in Production',
      statsYears: 'Years Building',
      statsProjects: 'Projects Delivered',
      statsSkills: 'Proven Skills',
      heroModelCaption: 'Real hardware, live 3D — the telemetry PCB I designed in KiCad, and the enclosure I modelled in Fusion 360, from bare board to finished product',
      heroModelCta: 'Explore the 3D models',
      skillsTitle: 'Four Disciplines, One Engineer',
      skillsSubtitle: 'Mechatronics means the whole machine. Pick a discipline to explore — every skill and number below comes from real, shipped projects.',
      skillsCta: 'Explore all skills & the projects behind them',
      categoryCta: 'Browse these skills',
      heroPause: 'Pause the 3D reel',
      heroPlay: 'Play the 3D reel',
      designStoryTitle: 'One Product, End to End',
      designStorySubtitle: 'The same telemetry gateway at every stage — schematic, board layout, 3D model, shipped product. Every image is exported from the real design files.',
      designStoryCta: 'See the full project',
      designSteps: {
        schematic: { label: 'Schematic', caption: 'Drawn in KiCad — ESP32, RS-485, RTC and microSD logging' },
        layout: { label: 'PCB Layout', caption: 'Two-layer board, routed in KiCad' },
        board: { label: '3D Board', caption: 'Rendered from the board’s CAD export' },
        product: { label: 'Final Product', caption: 'Assembled gateway, rendered from Fusion 360' }
      },
      pillarCoverAlts: {
        mechanical: 'AirFinity enclosure in 3D — translucent case showing the PCB inside',
        electrical: 'AirFinity — Industrial Compressor IoT',
        programming: 'Ez-RPH — Smart Lesson Planning for Modern Teachers',
        ai: 'OmniChat (AiphaChat)'
      },
      deployTitle: 'From Code to Production',
      deploySubtitle: 'The forward-deployed profile: I ship and run what I build — on servers, domains and networks I operate myself.',
      deploySteps: {
        build: { label: 'Build', caption: 'Full-stack across four disciplines — firmware, mobile and web apps, AI backends' },
        ship: { label: 'Ship', caption: 'CI/CD on a GitLab instance I host myself, deploying to Firebase Hosting and my production VPS' },
        run: { label: 'Run', caption: 'A production Linux VPS behind a reverse proxy with TLS, plus my own TrueNAS home server — Docker, PostgreSQL, ZFS' },
        reach: { label: 'Connect', caption: 'My own domains and DNS, Cloudflare Tunnel for public access, WireGuard and Tailscale VPNs for everything private' }
      },
      deployMediaAlt: 'Isometric illustration of my homelab — NAS, VPS and services joined by a mesh VPN',
      deployMediaCaption: 'Home server, mesh VPN, tunnel, production VPS — the network my live products run on',
      deployCta: 'See the infrastructure project'
    },
    // Projects page
    projects: {
      title: 'My Projects',
      sectionCaseStudies: 'Products & AI Systems',
      sectionCaseStudiesSub: 'flagship products and case studies',
      sectionRecent: 'More Recent Work',
      sectionRecentSub: 'experiments and side builds',
      sectionArchive: 'Earlier Engineering Projects',
      sectionArchiveSub: 'robotics, electronics and school-era builds'
    },
    // Project detail page
    projectDisplay: {
      back: 'Back to Projects',
      featuredBadge: 'Featured Product',
      galleryPrev: 'Previous',
      galleryNext: 'Next',
      galleryClose: 'Close',
      galleryExpand: 'Expand',
      galleryView: 'View',
      visitProduct: 'Visit Product',
      sourceCode: 'Source Code',
      about: 'About This Project',
      techStack: 'Tech Stack',
      conceptArt: 'Concept illustration',
      notFound: 'Project not found',
      viewer3dTitle: 'Interactive 3D Model',
      viewer3dLoading: 'Loading 3D model…',
      viewer3dHint: 'Drag to rotate · scroll to zoom',
      viewer3dPartPcb: 'PCB',
      viewer3dPartBody: 'Enclosure body',
      viewer3dPartCover: 'Cover',
      viewer3dPartPlates: 'Diffuser grates',
      viewer3dPartFrame: 'Inlet & frame',
      viewer3dPartHousing: 'Housing tube',
      viewer3dOpacity: 'Case opacity',
      viewer3dFallback: '3D view unavailable in this browser — see the project photos below.',
      techChipHint: 'See every project using this skill'
    },
    // Skills index page
    skillsPage: {
      title: 'Skills & Tools',
      subtitle: 'Every skill below is backed by real projects — click any entry to see the work behind it.',
      skillsWord: 'skills',
      skillWordSingular: 'skill',
      searchPlaceholder: 'Search a skill, tool or project… e.g. React, KiCad, ESP32',
      all: 'All',
      empty: 'No skills match your search.',
      clear: 'Clear search',
      projectSingular: 'project',
      projectPlural: 'projects',
      viewDetails: 'See the work behind this skill',
      detailBack: 'All skills',
      detailEvidence: 'Projects that prove it',
      detailNotFound: 'Skill not found.',
      model3dTitle: 'Interactive 3D model',
      showcaseCaptions: {
        'airfinity-enclosure': 'The AirFinity gateway enclosure I modelled in Fusion 360 — drag to rotate, toggle parts, and fade the case to see inside.',
        'airfinity-pcb': 'The AirFinity telemetry PCB I designed in KiCad (ESP32 + RS-485 + RTC + SD logging) — drag to rotate and inspect.',
        'diffuser-assembly': 'A stainless-steel industrial air diffuser I designed in Fusion 360 — five tilted collision grates inside a Ø124 mm housing. Toggle the layers and fade the housing to see the cross-flow baffle stack.',
        'printed-enclosure': 'The AirFinity gateway enclosure I 3D-printed — body and cover as separate printed parts. Toggle them and drag to rotate.'
      },
      categories: {
        software: 'Software & Web',
        ai: 'AI & Data',
        mobile: 'Mobile & Cross-Platform',
        embedded: 'Embedded & IoT',
        electronics: 'Electronics & PCB',
        cad: '3D, CAD & Making',
        robotics: 'Robotics & Automation',
        devops: 'Cloud & DevOps'
      },
      pillars: {
        mechanical: {
          name: 'Mechanical',
          tagline: 'CAD design, 3D printing, robotics and fabrication'
        },
        electrical: {
          name: 'Electrical',
          tagline: 'Circuit design, PCBs, embedded systems and IoT'
        },
        programming: {
          name: 'Software',
          tagline: 'Web, mobile, backend and cloud operations'
        },
        ai: {
          name: 'AI',
          tagline: 'LLM orchestration, RAG pipelines and AI products'
        }
      }
    },
    // Ez-RPH vlog walkthrough
    ezrph: {
      filmTitle: 'Watch the story',
      moreTitle: 'And there is more',
      features: {
        f1: { title: 'PBD AutoFill extension', text: "A companion Chrome extension fills matched PBD assessment rows straight into the Ministry of Education's SPPB portal — after the teacher confirms each entry." },
        f2: { title: 'Timetable as the source of truth', text: 'Weekly plans are generated from the school timetable, with merged continuous slots and academic weeks anchored to the school year.' },
        f3: { title: 'PBD student assessment', text: 'Excel-style paste, school-scoped student rosters, and All-Profiles export for the whole class in one go.' },
        f4: { title: 'QR device pairing', text: 'Scan a QR code to pair devices and keep multi-year lesson data in sync.' },
        f5: { title: 'School admin panel', text: 'License generation, member management, consent flows and peer-review queues for headmasters.' },
        f6: { title: 'Reports & exports', text: 'Print-ready official-format PDFs plus CSV and Excel exports for records and inspections.' }
      },
      filmCaption: 'The official Ez-RPH film — 43 seconds, sound on.',
      chaptersTitle: 'How it works',
      chapters: {
        c1: {
          title: 'The problem — planning by hand',
          text: "Marking's done, the kids have gone home — and hundreds of thousands of Malaysian teachers are still writing tomorrow's RPH by hand. Ez-RPH was built to give that time back."
        },
        c2: {
          title: 'Standards built in',
          text: 'Pick your class and the KSSR and KSSM curriculum standards are already there — no retyping from circulars, just select and go.'
        },
        c3: {
          title: 'A week of lessons in minutes',
          text: 'Build an entire week of lesson plans in minutes with per-slot subjects, classes and objectives — the school timetable is the single source of truth.'
        },
        c4: {
          title: 'AI drafts, you decide',
          text: 'Let AI draft your activities and reflections, then accept or edit. The AI runs through managed cloud functions, so no API keys ever live on the device.'
        },
        c5: {
          title: 'Whole-school approval',
          text: "Headmasters review and approve the whole school's plans in one place, with peer-review queues and a full audit trail."
        },
        c6: {
          title: 'Offline-first, export-ready',
          text: 'Weak signal never stops you — everything works offline and syncs later, and one tap exports print-ready PDFs in the official format.'
        },
        c7: {
          title: 'PBD AutoFill — straight into the ministry portal',
          text: "A companion Chrome extension closes the loop with the government's SPPB portal: it scans the PBD page, matches students against Ez-RPH records, shows the teacher every matched row for review, then fills the confirmed TP values into the portal — no more retyping marks row by row. (Shown with anonymised demo data.)"
        }
      }
    },
    // Experience page
    experience: {
      title: 'My Experience',
      timeline: {
        productEngineer: {
          date: 'October 2024 - Present',
          company: 'Independent Product Engineering',
          type: 'Avantix Innovation collaboration · Gatal Tech since April 2025',
          summary: 'Started collaborating with Avantix Innovation in October 2024 to lead AirFinity product engineering, and continue that collaboration while operating through Gatal Tech since April 2025.',
          products: 'Own end-to-end engineering for OmniChat, Ez-RPH and pre-release beta SwipeLah through Gatal Tech, spanning product design, full-stack development, testing, deployment and operations.'
        },
        greenNature: {
          date: 'June 2024 - September 2024',
          company: 'Green Nature Class Sdn. Bhd.',
          type: 'Project Engineer · Penang, Malaysia',
          summary: 'Produced engineering handover documents, electrical drawings and mechanical designs, and coordinated site supervision and on-site electrical and mechanical testing.'
        },
        sevenSense: {
          date: 'July 2019 - February 2024',
          company: 'Seven Sense (Raspi Sekolah)',
          type: 'Robotics Instructor · Project-based',
          summary: 'Returned for intermittent teaching projects through February 2024, guiding hands-on Python, Scratch, C++, Raspberry Pi and Arduino robotics work.'
        },
        secondary: {
          school: 'SMJK KEAT HWA II, Alor Setar, Kedah',
          type: 'Secondary School',
          description: 'Pure science stream with ICT as additional subject.'
        },
        diploma: {
          school: 'UCSI University, Cheras, Kuala Lumpur',
          type: 'Diploma In Electrical & Electronics Engineering',
          description1: 'Vice President of Robotics and AI Club',
          description2: 'Built hands-on robotics and embedded-system projects.'
        },
        degree: {
          school: 'UCSI University, Cheras, Kuala Lumpur',
          type: 'Degree In Electrical & Electronics Engineering',
          role1: 'Vice President of Robotics and AI Club',
          role2: 'President of Robotics and AI Club',
          role3: 'Vice Treasurer of Robotics and AI Club'
        }
      }
    },
    // Footer
    footer: {
      copyright: '© 2026 alphakangaroo.com'
    }
  },
  zh: {
    // Navbar
    nav: {
      home: '首页',
      projects: '项目',
      skills: '技能',
      experience: '经历'
    },
    // Home page
    home: {
      greeting: '你好，我是',
      name: '张家慷',
      headline: 'AI 原生产品工程师',
      bio: '我横跨四大领域打造完整产品：使用 Fusion 360 进行机械设计、完成从原理图到 PCB 的电子设计、开发从固件到云端的软件，并构建生产级 AI 系统。从多租户 WhatsApp AI SaaS 到工业 IoT 遥测系统，我负责产品落地的全过程：梳理业务问题、设计架构、编写代码并维护线上运行——服务器与网络由我自己搭建和运维，域名与 DNS 也由我自己管理。',
      ctaPrimary: '查看案例研究',
      ctaSecondary: '浏览全部项目',
      openTo: '期待工作与合作机会',
      featuredTitle: '精选项目',
      featuredSubtitle: '由我设计、构建并运营的产品与系统',
      viewCaseStudy: '查看案例研究',
      statsProducts: '已上线产品',
      statsYears: '年实践经验',
      statsProjects: '交付项目',
      statsSkills: '经验证技能',
      heroModelCaption: '真实硬件，可交互 3D——我用 KiCad 设计的遥测 PCB 与 Fusion 360 建模的外壳，从裸板到完整成品。',
      heroModelCta: '探索 3D 模型',
      skillsTitle: '四大领域，一位工程师',
      skillsSubtitle: '机电一体化，意味着掌握整套系统。选择一个领域深入了解——下方每项技能与统计数据，都有真实交付的项目为证。',
      skillsCta: '浏览全部技能与背后的项目',
      categoryCta: '查看这类技能',
      heroPause: '暂停 3D 演示',
      heroPlay: '播放 3D 演示',
      designStoryTitle: '一个产品，从设计到成品',
      designStorySubtitle: '同一台遥测网关从原理图、PCB 布局、3D 模型到交付成品的完整过程——所有图片均由真实设计文件导出。',
      designStoryCta: '查看完整项目',
      designSteps: {
        schematic: { label: '原理图', caption: '使用 KiCad 绘制——集成 ESP32、RS-485、RTC 与 microSD 数据记录' },
        layout: { label: 'PCB 版图', caption: '双层 PCB，使用 KiCad 完成布线' },
        board: { label: 'PCB 3D 模型', caption: '由 PCB 的 CAD 导出文件渲染' },
        product: { label: '最终成品', caption: '组装后的网关，以 Fusion 360 渲染呈现' }
      },
      pillarCoverAlts: {
        mechanical: 'AirFinity 外壳 3D 视图——半透明外壳可见内部 PCB',
        electrical: 'AirFinity — Industrial Compressor IoT',
        programming: 'Ez-RPH——面向现代教师的智能教案平台',
        ai: 'OmniChat (AiphaChat)'
      },
      deployTitle: '从代码到上线',
      deploySubtitle: '具备 Forward Deployed Engineer（FDE）的完整能力：我构建的产品由我亲手部署和运维——服务器与网络自己搭建，域名与 DNS 自己管理。',
      deploySteps: {
        build: { label: '构建', caption: '横跨四大领域的全栈开发——固件、移动与 Web 应用、AI 后端' },
        ship: { label: '交付', caption: '在自建的 GitLab 上运行 CI/CD，部署到 Firebase Hosting 和我的生产 VPS' },
        run: { label: '运维', caption: '生产环境 Linux VPS（反向代理 + TLS），加上自建的 TrueNAS 家庭服务器——Docker、PostgreSQL、ZFS' },
        reach: { label: '联通', caption: '自有域名与 DNS 管理，Cloudflare Tunnel 对外发布服务，WireGuard 与 Tailscale VPN 保障私有访问' }
      },
      deployMediaAlt: '家庭实验室等距插画——NAS、VPS 与各项服务通过 Mesh VPN 相连',
      deployMediaCaption: '家庭服务器、Mesh VPN、隧道、生产 VPS——支撑我线上产品的一整套网络',
      deployCta: '查看基础设施项目'
    },
    // Projects page
    projects: {
      title: '我的项目',
      sectionCaseStudies: '产品与 AI 系统',
      sectionCaseStudiesSub: '旗舰产品与案例研究',
      sectionRecent: '更多近期项目',
      sectionRecentSub: '实验项目与业余开发',
      sectionArchive: '早期工程项目',
      sectionArchiveSub: '机器人、电子与学生时代的项目'
    },
    // Project detail page
    projectDisplay: {
      back: '返回项目列表',
      featuredBadge: '精选产品',
      galleryPrev: '上一张',
      galleryNext: '下一张',
      galleryClose: '关闭',
      galleryExpand: '放大',
      galleryView: '查看',
      visitProduct: '访问产品',
      sourceCode: '源代码',
      about: '关于此项目',
      techStack: '技术栈',
      conceptArt: '概念插画',
      notFound: '未找到该项目',
      viewer3dTitle: '交互式 3D 模型',
      viewer3dLoading: '正在加载 3D 模型…',
      viewer3dHint: '拖动旋转 · 滚轮缩放',
      viewer3dPartPcb: 'PCB',
      viewer3dPartBody: '外壳主体',
      viewer3dPartCover: '外壳盖板',
      viewer3dPartPlates: '扩散格栅',
      viewer3dPartFrame: '进气口与框架',
      viewer3dPartHousing: '筒形外壳',
      viewer3dOpacity: '外壳不透明度',
      viewer3dFallback: '此浏览器无法显示 3D 视图——请查看下方的项目照片。',
      techChipHint: '查看使用此技能的所有项目'
    },
    // Skills index page
    skillsPage: {
      title: '技能与工具',
      subtitle: '以下每项技能都有真实项目为证——点击任意一项，即可查看相关项目。',
      skillsWord: '项技能',
      skillWordSingular: '项技能',
      searchPlaceholder: '搜索技能、工具或项目…，如 React、KiCad、ESP32',
      all: '全部',
      empty: '没有匹配的技能。',
      clear: '清除搜索',
      projectSingular: '个项目',
      projectPlural: '个项目',
      viewDetails: '查看证明这项技能的项目',
      detailBack: '全部技能',
      detailEvidence: '相关项目成果',
      detailNotFound: '找不到该技能。',
      model3dTitle: '交互式 3D 模型',
      showcaseCaptions: {
        'airfinity-enclosure': '我使用 Fusion 360 建模的 AirFinity 网关外壳——拖动即可旋转，切换部件并调节外壳透明度，即可查看内部结构。',
        'airfinity-pcb': '我使用 KiCad 设计的 AirFinity 遥测 PCB（ESP32 + RS-485 + RTC + microSD 日志记录）——拖动旋转即可查看细节。',
        'diffuser-assembly': '我使用 Fusion 360 设计的不锈钢工业空气散流器——Ø124 mm 筒体内设有五层倾斜撞击格栅。切换各层并调节筒体透明度，即可查看横向气流挡板组。',
        'printed-enclosure': '我 3D 打印的 AirFinity 网关外壳——主体与盖板分别打印成独立部件，可切换显示并拖动旋转。'
      },
      categories: {
        software: '软件与 Web',
        ai: 'AI 与数据',
        mobile: '移动与跨平台',
        embedded: '嵌入式与 IoT',
        electronics: '电子与 PCB',
        cad: '3D、CAD 与制造',
        robotics: '机器人与自动化',
        devops: '云与运维'
      },
      pillars: {
        mechanical: {
          name: '机械',
          tagline: 'CAD 设计、3D 打印、机器人与制造'
        },
        electrical: {
          name: '电气与电子',
          tagline: '电路设计、PCB、嵌入式系统与 IoT'
        },
        programming: {
          name: '软件',
          tagline: 'Web、移动端、后端与云运维'
        },
        ai: {
          name: 'AI',
          tagline: 'LLM 编排、RAG 工作流与 AI 产品'
        }
      }
    },
    // Ez-RPH vlog walkthrough
    ezrph: {
      filmTitle: '观看产品故事',
      moreTitle: '不止于此',
      features: {
        f1: { title: 'PBD AutoFill 扩展程序', text: '配套 Chrome 扩展程序会在教师逐项确认后，将匹配的 PBD 评估记录直接填入教育部的 SPPB 门户。' },
        f2: { title: '以课程表为准', text: '周教案根据学校课程表生成，连续时段自动合并，教学周则按学年编排。' },
        f3: { title: 'PBD 学生评估', text: '支持像 Excel 一样批量粘贴、按学校管理学生名册，并一键导出全班所有档案（All-Profiles）。' },
        f4: { title: '扫码配对设备', text: '扫描二维码即可配对设备，多学年教案数据保持同步。' },
        f5: { title: '学校管理后台', text: '为校长提供授权码生成、成员管理、同意流程和同侪审核队列。' },
        f6: { title: '报表与导出', text: '符合官方格式、可直接打印的 PDF，并可导出 CSV 和 Excel，便于存档与检查。' }
      },
      filmCaption: 'Ez-RPH 官方短片——43 秒，请开启声音。',
      chaptersTitle: '使用流程',
      chapters: {
        c1: {
          title: '痛点——手写教案',
          text: '批改完成、学生放学后，数十万名马来西亚教师仍在手写第二天的教案（RPH）。Ez-RPH 正是为了把这段时间还给教师而打造。'
        },
        c2: {
          title: '内置课程标准',
          text: '选择班级后，即可直接使用内置的 KSSR 与 KSSM 课程标准——无需从通告中重新录入，选好即可开始。'
        },
        c3: {
          title: '几分钟完成一周教案',
          text: '只需几分钟，即可按每个时段的科目、班级和教学目标完成整周教案——一切以学校课程表为准。'
        },
        c4: {
          title: 'AI 起草，老师做主',
          text: '让 AI 起草教学活动与反思内容，再由教师采纳或修改。AI 通过托管云函数运行，因此设备上无需存放 API 密钥。'
        },
        c5: {
          title: '全校审批',
          text: '校长在一个界面审阅并批准全校的教案，支持同侪评审队列与完整的审计记录。'
        },
        c6: {
          title: '离线优先，随时导出',
          text: '网络信号再弱也不耽误工作——所有功能均可离线使用，联网后再自动同步；轻点一下，即可导出符合官方格式、可直接打印的 PDF。'
        },
        c7: {
          title: 'PBD AutoFill——直接填写教育部 SPPB 门户',
          text: '配套 Chrome 扩展程序打通与 SPPB 官方门户的最后一环：扫描 PBD 页面，将学生与 Ez-RPH 记录自动匹配，列出每条匹配结果供教师复核，再把确认后的 TP 值填入门户——无需逐行重复录入成绩。（画面使用匿名演示数据。）'
        }
      }
    },
    // Experience page
    experience: {
      title: '我的经历',
      timeline: {
        productEngineer: {
          date: '2024 年 10 月至今',
          company: '独立产品工程',
          type: '与 Avantix Innovation 合作 · 2025 年 4 月起通过 Gatal Tech 运营',
          summary: '2024 年 10 月开始与 Avantix Innovation 合作并主导 AirFinity 产品工程；自 2025 年 4 月起通过 Gatal Tech 运营，同时持续这项合作。',
          products: '通过 Gatal Tech 端到端负责 OmniChat、Ez-RPH 与预发布测试版 SwipeLah，涵盖产品设计、全栈开发、测试、部署与运维。'
        },
        greenNature: {
          date: '2024 年 6 月至 2024 年 9 月',
          company: 'Green Nature Class Sdn. Bhd.',
          type: '项目工程师 · 马来西亚槟城',
          summary: '编制工程移交文件、电气图纸与机械设计，并协调现场监督以及电气和机械测试。'
        },
        sevenSense: {
          date: '2019 年 7 月至 2024 年 2 月',
          company: 'Seven Sense（Raspi Sekolah）',
          type: '机器人课程讲师 · 项目制',
          summary: '以间歇项目形式持续授课至 2024 年 2 月，指导 Python、Scratch、C++、Raspberry Pi 与 Arduino 机器人实践。'
        },
        secondary: {
          school: '吉华国民型华文中学二校，亚罗士打，吉打',
          type: '中学',
          description: '主修纯理科，并选修信息与通信技术。'
        },
        diploma: {
          school: 'UCSI 大学，蕉赖，吉隆坡',
          type: '电气与电子工程文凭',
          description1: '机器人与 AI 学会副主席',
          description2: '完成机器人与嵌入式系统实践项目。'
        },
        degree: {
          school: 'UCSI 大学，蕉赖，吉隆坡',
          type: '电气与电子工程学士学位',
          role1: '机器人与 AI 学会副主席',
          role2: '机器人与 AI 学会主席',
          role3: '机器人与 AI 学会副财政'
        }
      }
    },
    // Footer
    footer: {
      copyright: '© 2026 alphakangaroo.com'
    }
  },
  ms: {
    // Navbar
    nav: {
      home: 'Utama',
      projects: 'Projek',
      skills: 'Kemahiran',
      experience: 'Pengalaman'
    },
    // Home page
    home: {
      greeting: 'Hai, saya',
      name: 'Teoh Jia Kang',
      headline: 'Jurutera Produk AI-Native',
      bio: 'Saya membangunkan produk lengkap merentasi empat disiplin — reka bentuk mekanikal menggunakan Fusion 360, elektronik daripada skematik hingga PCB, perisian daripada perisian tegar hingga awan, serta sistem AI untuk kegunaan sebenar. Daripada SaaS AI berbilang penyewa untuk WhatsApp hingga telemetri IoT industri, saya mengendalikan keseluruhan kitaran produk: masalah perniagaan, seni bina, kod dan operasi sebenar — dilancarkan pada pelayan dan rangkaian yang saya kendalikan sendiri, dengan domain milik saya.',
      ctaPrimary: 'Lihat Kajian Kes',
      ctaSecondary: 'Lihat Semua Projek',
      openTo: 'Terbuka kepada peluang baharu',
      featuredTitle: 'Projek Pilihan',
      featuredSubtitle: 'Produk dan sistem yang saya reka, bina dan kendalikan',
      viewCaseStudy: 'Lihat Kajian Kes',
      statsProducts: 'Produk dalam operasi',
      statsYears: 'Tahun pengalaman',
      statsProjects: 'Projek Disiapkan',
      statsSkills: 'Kemahiran Terbukti',
      heroModelCaption: 'Perkakasan sebenar, model 3D interaktif — PCB telemetri yang saya reka menggunakan KiCad dan perumah yang saya modelkan menggunakan Fusion 360, daripada papan kosong hingga produk siap',
      heroModelCta: 'Terokai model 3D',
      skillsTitle: 'Empat Disiplin, Seorang Jurutera',
      skillsSubtitle: 'Mekatronik merangkumi keseluruhan sistem. Pilih satu disiplin untuk diterokai — setiap kemahiran dan angka di bawah berasaskan projek sebenar yang telah saya siapkan.',
      skillsCta: 'Terokai semua kemahiran & projek berkaitan',
      categoryCta: 'Semak imbas kemahiran ini',
      heroPause: 'Jeda paparan 3D',
      heroPlay: 'Mainkan paparan 3D',
      designStoryTitle: 'Satu Produk, Dari Awal Hingga Akhir',
      designStorySubtitle: 'Gerbang telemetri yang sama pada setiap peringkat — daripada skematik dan susun atur PCB kepada model 3D dan produk siap. Setiap imej dieksport daripada fail reka bentuk sebenar.',
      designStoryCta: 'Lihat projek penuh',
      designSteps: {
        schematic: { label: 'Skematik', caption: 'Dilukis menggunakan KiCad — ESP32, RS-485, RTC dan rakaman data pada microSD' },
        layout: { label: 'Susun Atur PCB', caption: 'PCB dua lapisan dengan laluan litar disusun menggunakan KiCad' },
        board: { label: 'Model 3D PCB', caption: 'Dirender daripada eksport CAD PCB' },
        product: { label: 'Produk Akhir', caption: 'Gerbang yang siap dipasang, dirender menggunakan Fusion 360' }
      },
      pillarCoverAlts: {
        mechanical: 'Perumah AirFinity dalam 3D — perumah lut sinar menampakkan PCB di dalamnya',
        electrical: 'AirFinity — Industrial Compressor IoT',
        programming: 'Ez-RPH — perancangan RPH pintar untuk guru moden',
        ai: 'OmniChat (AiphaChat)'
      },
      deployTitle: 'Daripada Kod ke Operasi Sebenar',
      deploySubtitle: 'Profil forward-deployed engineer (FDE): saya melancarkan dan mengendalikan sendiri apa yang saya bina — pada pelayan, domain dan rangkaian yang saya uruskan sendiri.',
      deploySteps: {
        build: { label: 'Bina', caption: 'Pembangunan penuh merentasi empat disiplin — perisian tegar, aplikasi mudah alih dan web, backend AI' },
        ship: { label: 'Lancar', caption: 'CI/CD pada GitLab yang saya hoskan sendiri, dilancarkan ke Firebase Hosting dan VPS saya' },
        run: { label: 'Kendali', caption: 'VPS Linux untuk operasi sebenar dengan reverse proxy dan TLS, serta pelayan rumah TrueNAS saya sendiri — Docker, PostgreSQL, ZFS' },
        reach: { label: 'Hubung', caption: 'Domain sendiri dan pengurusan DNS, Cloudflare Tunnel untuk akses awam, VPN WireGuard serta Tailscale untuk akses peribadi' }
      },
      deployMediaAlt: 'Ilustrasi isometrik homelab saya — NAS, VPS dan perkhidmatan dihubungkan oleh VPN mesh',
      deployMediaCaption: 'Pelayan rumah, VPN mesh, terowong, VPS operasi sebenar — rangkaian yang menggerakkan produk saya',
      deployCta: 'Lihat projek infrastruktur'
    },
    // Projects page
    projects: {
      title: 'Projek Saya',
      sectionCaseStudies: 'Produk & Sistem AI',
      sectionCaseStudiesSub: 'produk utama dan kajian kes',
      sectionRecent: 'Projek Terkini Lain',
      sectionRecentSub: 'eksperimen dan projek sampingan',
      sectionArchive: 'Projek Kejuruteraan Terdahulu',
      sectionArchiveSub: 'robotik, elektronik dan projek zaman sekolah'
    },
    // Project detail page
    projectDisplay: {
      back: 'Kembali ke Projek',
      featuredBadge: 'Produk Pilihan',
      galleryPrev: 'Sebelumnya',
      galleryNext: 'Seterusnya',
      galleryClose: 'Tutup',
      galleryExpand: 'Besarkan',
      galleryView: 'Lihat',
      visitProduct: 'Lawati Produk',
      sourceCode: 'Kod Sumber',
      about: 'Mengenai Projek Ini',
      techStack: 'Teknologi yang Digunakan',
      conceptArt: 'Ilustrasi konsep',
      notFound: 'Projek tidak dijumpai',
      viewer3dTitle: 'Model 3D Interaktif',
      viewer3dLoading: 'Memuatkan model 3D…',
      viewer3dHint: 'Seret untuk putar · tatal untuk zum',
      viewer3dPartPcb: 'PCB',
      viewer3dPartBody: 'Badan perumah',
      viewer3dPartCover: 'Penutup',
      viewer3dPartPlates: 'Jeriji peresap',
      viewer3dPartFrame: 'Salur masuk & rangka',
      viewer3dPartHousing: 'Tiub perumah',
      viewer3dOpacity: 'Kelegapan perumah',
      viewer3dFallback: 'Paparan 3D tidak tersedia dalam pelayar ini — lihat foto projek di bawah.',
      techChipHint: 'Lihat semua projek yang menggunakan kemahiran ini'
    },
    // Skills index page
    skillsPage: {
      title: 'Kemahiran & Alatan',
      subtitle: 'Setiap kemahiran di bawah dibuktikan melalui projek sebenar — klik mana-mana kemahiran untuk melihat projek berkaitan.',
      skillsWord: 'kemahiran',
      skillWordSingular: 'kemahiran',
      searchPlaceholder: 'Cari kemahiran, alatan atau projek… cth. React, KiCad, ESP32',
      all: 'Semua',
      empty: 'Tiada kemahiran sepadan dengan carian anda.',
      clear: 'Kosongkan carian',
      projectSingular: 'projek',
      projectPlural: 'projek',
      viewDetails: 'Lihat projek yang membuktikan kemahiran ini',
      detailBack: 'Semua kemahiran',
      detailEvidence: 'Bukti melalui projek sebenar',
      detailNotFound: 'Kemahiran tidak dijumpai.',
      model3dTitle: 'Model 3D interaktif',
      showcaseCaptions: {
        'airfinity-enclosure': 'Perumah gerbang AirFinity yang saya modelkan menggunakan Fusion 360 — seret untuk memutar, paparkan atau sembunyikan bahagian, dan laraskan kelegapan perumah untuk melihat bahagian dalam.',
        'airfinity-pcb': 'PCB telemetri AirFinity yang saya reka menggunakan KiCad (ESP32 + RS-485 + RTC + rakaman data pada microSD) — seret untuk memutar dan meneliti reka bentuknya.',
        'diffuser-assembly': 'Peresap udara industri keluli tahan karat yang saya reka menggunakan Fusion 360 — lima jeriji hentaman condong di dalam perumah Ø124 mm. Paparkan atau sembunyikan setiap lapisan dan laraskan kelegapan perumah untuk melihat susunan sesekat aliran silang.',
        'printed-enclosure': 'Perumah gerbang AirFinity yang saya cetak 3D — badan dan penutup dicetak sebagai dua bahagian berasingan. Paparkan atau sembunyikan bahagian, kemudian seret untuk memutar.'
      },
      categories: {
        software: 'Perisian & Web',
        ai: 'AI & Data',
        mobile: 'Mudah Alih & Merentas Platform',
        embedded: 'Sistem Terbenam & IoT',
        electronics: 'Elektronik & PCB',
        cad: '3D, CAD & Fabrikasi',
        robotics: 'Robotik & Automasi',
        devops: 'Awan & DevOps'
      },
      pillars: {
        mechanical: {
          name: 'Mekanikal',
          tagline: 'Reka bentuk CAD, percetakan 3D, robotik dan fabrikasi'
        },
        electrical: {
          name: 'Elektrik & Elektronik',
          tagline: 'Reka bentuk litar, PCB, sistem terbenam dan IoT'
        },
        programming: {
          name: 'Perisian',
          tagline: 'Web, aplikasi mudah alih, backend dan operasi awan'
        },
        ai: {
          name: 'AI',
          tagline: 'Orkestrasi LLM, aliran kerja RAG dan produk AI'
        }
      }
    },
    // Ez-RPH vlog walkthrough
    ezrph: {
      filmTitle: 'Tonton kisahnya',
      moreTitle: 'Dan banyak lagi',
      features: {
        f1: { title: 'Sambungan Chrome PBD AutoFill', text: 'Sambungan Chrome pelengkap mengisi baris penilaian PBD yang sepadan terus dalam portal SPPB KPM selepas guru mengesahkan setiap entri.' },
        f2: { title: 'Jadual waktu sebagai sumber rujukan', text: 'Rancangan mingguan dijana daripada jadual waktu sekolah; slot bersambung digabungkan dan minggu akademik diselaraskan dengan tahun persekolahan.' },
        f3: { title: 'Penilaian murid PBD', text: 'Tampal data seperti dalam Excel, urus senarai murid mengikut sekolah, dan eksport All-Profiles untuk seluruh kelas sekali gus.' },
        f4: { title: 'Gandingan peranti melalui QR', text: 'Imbas kod QR untuk menggandingkan peranti dan memastikan data RPH berbilang tahun sentiasa segerak.' },
        f5: { title: 'Panel pentadbir sekolah', text: 'Penjanaan lesen, pengurusan ahli, proses persetujuan dan giliran semakan rakan sebaya untuk kegunaan guru besar.' },
        f6: { title: 'Laporan & eksport', text: 'PDF sedia cetak dalam format rasmi, serta eksport CSV dan Excel untuk rekod dan pemeriksaan.' }
      },
      filmCaption: 'Video rasmi Ez-RPH — 43 saat; hidupkan bunyi.',
      chaptersTitle: 'Bagaimana ia berfungsi',
      chapters: {
        c1: {
          title: 'Masalah — menyediakan RPH secara manual',
          text: 'Selepas selesai menyemak kerja murid dan mereka sudah pulang, ratusan ribu guru di Malaysia masih menyediakan RPH untuk keesokan hari secara manual. Ez-RPH dibina untuk mengembalikan masa itu kepada mereka.'
        },
        c2: {
          title: 'Standard kurikulum tersedia',
          text: 'Pilih kelas anda dan standard kurikulum KSSR serta KSSM terus tersedia — tidak perlu menaip semula daripada pekeliling; hanya pilih dan teruskan.'
        },
        c3: {
          title: 'Rancangan seminggu dalam beberapa minit',
          text: 'Hasilkan rancangan pengajaran seminggu penuh dalam beberapa minit, lengkap dengan mata pelajaran, kelas dan objektif bagi setiap slot — semuanya berpandukan jadual waktu sekolah sebagai sumber rujukan.'
        },
        c4: {
          title: 'AI merangka, anda tentukan',
          text: 'AI menyediakan draf aktiviti dan refleksi untuk anda terima atau sunting. AI beroperasi melalui fungsi awan terurus — tiada kunci API disimpan pada peranti.'
        },
        c5: {
          title: 'Kelulusan seluruh sekolah',
          text: 'Guru besar menyemak dan meluluskan semua RPH sekolah di satu tempat, dengan giliran semakan rakan sebaya dan jejak audit yang lengkap.'
        },
        c6: {
          title: 'Boleh digunakan di luar talian, sedia dieksport',
          text: 'Sambungan lemah tidak mengganggu kerja — semuanya berfungsi di luar talian dan disegerakkan kemudian; dengan satu ketikan, PDF sedia cetak dalam format rasmi boleh dieksport.'
        },
        c7: {
          title: 'PBD AutoFill — terus ke portal SPPB KPM',
          text: 'Sambungan Chrome pelengkap melengkapkan aliran kerja dengan portal SPPB KPM: ia mengimbas halaman PBD, memadankan murid dengan rekod Ez-RPH, memaparkan setiap baris padanan untuk disemak oleh guru, kemudian mengisi nilai TP yang telah disahkan terus ke portal — tidak perlu lagi menaip semula markah satu demi satu. (Menggunakan data demo yang dinyahnamakan.)'
        }
      }
    },
    // Experience page
    experience: {
      title: 'Pengalaman Saya',
      timeline: {
        productEngineer: {
          date: 'Oktober 2024 - Kini',
          company: 'Kejuruteraan Produk Bebas',
          type: 'Kerjasama Avantix Innovation · Gatal Tech sejak April 2025',
          summary: 'Mula bekerjasama dengan Avantix Innovation pada Oktober 2024 untuk menerajui kejuruteraan produk AirFinity, dan meneruskan kerjasama itu sambil beroperasi melalui Gatal Tech sejak April 2025.',
          products: 'Mengendalikan kejuruteraan hujung ke hujung bagi OmniChat, Ez-RPH dan SwipeLah versi beta pra-keluaran melalui Gatal Tech, daripada reka bentuk produk dan pembangunan full-stack hingga ujian, pelancaran dan operasi.'
        },
        greenNature: {
          date: 'Jun 2024 - September 2024',
          company: 'Green Nature Class Sdn. Bhd.',
          type: 'Jurutera Projek · Pulau Pinang, Malaysia',
          summary: 'Menyediakan dokumen serahan kejuruteraan, lukisan elektrik dan reka bentuk mekanikal, serta menyelaras penyeliaan tapak dan ujian elektrik serta mekanikal di lokasi.'
        },
        sevenSense: {
          date: 'Julai 2019 - Februari 2024',
          company: 'Seven Sense (Raspi Sekolah)',
          type: 'Pengajar Robotik · Berasaskan projek',
          summary: 'Kembali mengajar bagi projek berkala hingga Februari 2024, membimbing kerja robotik praktikal menggunakan Python, Scratch, C++, Raspberry Pi dan Arduino.'
        },
        secondary: {
          school: 'SMJK KEAT HWA II, Alor Setar, Kedah',
          type: 'Sekolah Menengah',
          description: 'Aliran sains tulen dengan ICT sebagai subjek tambahan.'
        },
        diploma: {
          school: 'Universiti UCSI, Cheras, Kuala Lumpur',
          type: 'Diploma Kejuruteraan Elektrik & Elektronik',
          description1: 'Naib Presiden Kelab Robotik dan AI',
          description2: 'Membina projek robotik dan sistem terbenam secara praktikal.'
        },
        degree: {
          school: 'Universiti UCSI, Cheras, Kuala Lumpur',
          type: 'Ijazah Kejuruteraan Elektrik & Elektronik',
          role1: 'Naib Presiden Kelab Robotik dan AI',
          role2: 'Presiden Kelab Robotik dan AI',
          role3: 'Naib Bendahari Kelab Robotik dan AI'
        }
      }
    },
    // Footer
    footer: {
      copyright: '© 2026 alphakangaroo.com'
    }
  }
};

export function t(translations, language, key) {
  const keys = key.split('.');
  let result = translations[language];
  for (const k of keys) {
    if (result && result[k] !== undefined) {
      result = result[k];
    } else {
      return key;
    }
  }
  return result;
}
